import { useState, useEffect } from 'react'
import { auth, players as playersApi, teams as teamsApi } from '../lib/api'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export default function Login({ onLogin, user }) {
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [position, setPosition] = useState('player')
  const [carrier, setCarrier] = useState('')
  const [wantsSms, setWantsSms] = useState(true)
  const [searchParams] = useSearchParams()
  const intent = searchParams.get('intent')
  const signupParam = searchParams.get('signup')
  const typeParam = searchParams.get('type') // 'org_owner' or 'player'
  const [userType, setUserType] = useState(typeParam || '') // Pre-fill from URL
  const [showTypeSelection, setShowTypeSelection] = useState(!typeParam) // Skip if type provided
  const [isSignUp, setIsSignUp] = useState(signupParam === 'true')
  const [message, setMessage] = useState('')
  const navigate = useNavigate()

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin')
      } else {
        navigate('/dashboard')
      }
    }
  }, [user, navigate])

  // Listen for auth success from magic link tab
  useEffect(() => {
    const handleAuthSuccess = (authUser) => {
      // Try to close this tab since auth happened in another tab
      window.close()
      // If window.close() doesn't work (not opened by script), redirect instead
      // Use the user data to redirect to the correct page directly (avoid flash)
      setTimeout(() => {
        if (authUser && authUser.role === 'admin') {
          window.location.href = '/admin'
        } else {
          window.location.href = '/dashboard'
        }
      }, 100)
    }

    // BroadcastChannel listener
    let channel
    try {
      channel = new BroadcastChannel('iceops_auth')
      channel.onmessage = (event) => {
        if (event.data?.type === 'auth_success') {
          handleAuthSuccess(event.data.user)
        }
      }
    } catch (e) {
      // BroadcastChannel not supported
    }

    // localStorage fallback listener
    const handleStorage = (event) => {
      if (event.key === 'iceops_auth_event') {
        handleAuthSuccess(null)
      }
    }
    window.addEventListener('storage', handleStorage)

    return () => {
      if (channel) channel.close()
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const openSubRequestEmail = async () => {
    try {
      const user = auth.getUser()

      // Fetch all players to find which teams this user is on
      const allPlayers = await playersApi.getAll()
      const userPlayers = allPlayers.filter(p => p.user_id === user.id)

      if (userPlayers.length === 0) {
        setMessage('You are not assigned to any team yet. Please contact your league administrator.')
        return
      }

      // Get all teams
      const allTeams = await teamsApi.getAll()
      const userTeams = allTeams.filter(t => userPlayers.some(p => p.team_id === t.id))

      if (userTeams.length === 0) {
        setMessage('No team found. Please contact your league administrator.')
        return
      }

      // For now, use the first team
      const team = userTeams[0]

      // Get team captains
      const captainsResponse = await fetch(`${API_URL}/api/team-captains/team/${team.id}`)
      const captains = await captainsResponse.json()

      // Get league managers/owners
      const managersResponse = await fetch(`${API_URL}/api/leagues/${team.league_id}/managers`)
      const managers = await managersResponse.json()

      // Collect all email addresses
      const emails = []
      captains.forEach(captain => {
        if (captain.email) emails.push(captain.email)
      })
      managers.forEach(manager => {
        if (manager.email) emails.push(manager.email)
      })

      // Remove duplicates
      const uniqueEmails = [...new Set(emails)]

      if (uniqueEmails.length === 0) {
        setMessage('No captain or league manager emails found. Please contact your team directly.')
        return
      }

      // Create mailto link
      const subject = encodeURIComponent(`Sub Request - ${team.name}`)
      const body = encodeURIComponent(`Hi,\n\nI need to request a substitute for an upcoming game.\n\nTeam: ${team.name}\n\nPlease let me know which game you'd like me to find a sub for.\n\nThanks,\n${user.name}`)
      const mailtoLink = `mailto:${uniqueEmails.join(',')}?subject=${subject}&body=${body}`

      window.location.href = mailtoLink

      // Navigate to home after opening email
      setTimeout(() => {
        navigate('/')
      }, 500)

    } catch (error) {
      console.error('Error opening sub request email:', error)
      setMessage('Error preparing email. Please contact your team captain directly.')
    }
  }

  const handleAuth = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')

    try {
      if (isSignUp) {
        // All signups use passwordless magic link
        await auth.signUpPasswordless(email, name, phone, userType, wantsSms ? carrier : null)

        // Show role-specific success message
        let successMessage = 'Account created! Check your email for a sign-in link.'
        if (userType === 'org_owner') {
          successMessage = `Welcome aboard, ${name}! Check your email for a secure sign-in link. Once you click it, you'll be guided through setting up your organization and first league.`
        } else if (userType === 'player') {
          successMessage = `Welcome, ${name}! Check your email for a sign-in link. After signing in, you can discover and join teams in your league.`
        } else if (userType === 'referee') {
          successMessage = `Welcome, ${name}! Check your email for a sign-in link. After signing in, you can create your referee profile and get discovered by leagues.`
        }

        setMessage(successMessage)
        setLoading(false)

        // Clear form but stay on page so user can see the message
        setEmail('')
        setName('')
        setPhone('')
        return
      } else {
        // Sign-in uses magic link
        await auth.requestMagicLink(email)
        setMessage('Check your email! We sent you a sign-in link.')
        setLoading(false)

        // Clear form and redirect after showing success message
        setTimeout(() => {
          setEmail('')
          navigate('/')
        }, 2000)
        return
      }
    } catch (error) {
      setMessage(error.message || 'Authentication failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Back to Home button */}
      <div className="mb-6">
        <Link to="/" className="btn-back">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
      </div>

      <div className="max-w-md mx-auto">
        <div className="card">
        {/* Signup Type Badge */}
        {isSignUp && userType && (
          <div className="text-center mb-6">
            {userType === 'org_owner' ? (
              <div className="inline-flex flex-col items-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-ice-100 text-ice-800 rounded-full text-sm font-semibold mb-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  League Manager
                </div>
                <p className="text-sm text-slate-600">14-day free trial • No credit card required</p>
              </div>
            ) : userType === 'referee' ? (
              <div className="inline-flex flex-col items-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-900 rounded-full text-sm font-semibold mb-2 border-2 border-slate-900">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Referee Sign Up
                </div>
                <p className="text-sm text-slate-600">Create your profile • Get discovered by leagues</p>
              </div>
            ) : (
              <div className="inline-flex flex-col items-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold mb-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Player Sign Up
                </div>
                <p className="text-sm text-slate-600">Always free • Join your team</p>
              </div>
            )}
          </div>
        )}

        {!isSignUp && (
          <h1 className="section-header text-center mb-4 sm:mb-6 text-lg sm:text-xl">
            Sign In
          </h1>
        )}

        {message && (
          <div className={`alert ${
            message.includes('Check your email') || message.includes('Account created')
              ? 'alert-success'
              : 'alert-error'
          }`}>
            {message}
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-3 sm:space-y-4">
          {isSignUp && showTypeSelection && (
            <div>
              <label className="label text-center mb-4">Choose your account type</label>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => {
                    setUserType('org_owner')
                    setShowTypeSelection(false)
                  }}
                  className="w-full p-5 text-left border-2 border-ice-200 bg-ice-50 rounded-lg hover:border-ice-600 hover:bg-ice-100 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-ice-600 rounded-full flex items-center justify-center text-white group-hover:bg-ice-700 transition-colors">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-900 text-lg">League Manager</div>
                      <div className="text-sm text-slate-700 mt-1">
                        Start a 14-day free trial to manage teams, schedules, and players
                      </div>
                      <div className="mt-2 inline-flex items-center text-xs font-semibold text-ice-700">
                        Free trial • No credit card required →
                      </div>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUserType('player')
                    setShowTypeSelection(false)
                  }}
                  className="w-full p-5 text-left border-2 border-green-200 bg-green-50 rounded-lg hover:border-green-600 hover:bg-green-100 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white group-hover:bg-green-700 transition-colors">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-900 text-lg">Player</div>
                      <div className="text-sm text-slate-700 mt-1">
                        Join your team to view schedules, receive notifications, and track stats
                      </div>
                      <div className="mt-2 inline-flex items-center text-xs font-semibold text-green-700">
                        Always free →
                      </div>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUserType('referee')
                    setShowTypeSelection(false)
                  }}
                  className="w-full p-5 text-left border-2 border-slate-300 bg-slate-50 rounded-lg hover:border-slate-900 hover:bg-slate-100 transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white group-hover:bg-black transition-colors">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-900 text-lg">Referee / Official</div>
                      <div className="text-sm text-slate-700 mt-1">
                        Create your profile to be discovered by leagues seeking officials
                      </div>
                      <div className="mt-2 inline-flex items-center text-xs font-semibold text-slate-900">
                        Get booked by leagues →
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {isSignUp && !showTypeSelection && (
            <>
              <div>
                <label className="label">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label className="label">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input"
                  placeholder="(555) 123-4567"
                />
                {phone && (
                  <div className="mt-3 space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={wantsSms}
                        onChange={(e) => setWantsSms(e.target.checked)}
                        className="w-4 h-4 text-ice-600 border-slate-300 rounded focus:ring-ice-500"
                      />
                      <span className="text-sm text-slate-700">
                        Send me game reminders via text message
                      </span>
                    </label>
                    {wantsSms && (
                      <div>
                        <label className="label text-sm">Mobile Carrier</label>
                        <select
                          value={carrier}
                          onChange={(e) => setCarrier(e.target.value)}
                          className="input text-sm"
                          required={wantsSms}
                        >
                          <option value="">Select your carrier...</option>
                          <option value="verizon">Verizon</option>
                          <option value="att">AT&T</option>
                          <option value="tmobile">T-Mobile</option>
                          <option value="sprint">Sprint</option>
                          <option value="uscellular">US Cellular</option>
                          <option value="metro">Metro by T-Mobile</option>
                          <option value="boost">Boost Mobile</option>
                          <option value="cricket">Cricket Wireless</option>
                          <option value="fi">Google Fi</option>
                          <option value="mint">Mint Mobile</option>
                          <option value="xfinity">Xfinity Mobile</option>
                          <option value="visible">Visible</option>
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          Required for text message reminders
                        </p>
                      </div>
                    )}
                    {!wantsSms && (
                      <p className="text-xs text-slate-500">
                        You'll receive game reminders via email only
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Only show position field for player signups */}
              {userType === 'player' && (
                <div>
                  <label className="label">Position</label>
                  <select
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className="input"
                    required
                  >
                    <option value="player">Player</option>
                    <option value="goalie">Goalie</option>
                  </select>
                </div>
              )}
            </>
          )}

          {(!isSignUp || !showTypeSelection) && (
            <>
              <div>
                <label className="label">Email</label>
                <input
                  name="username"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50 text-sm sm:text-base"
              >
                {loading
                  ? 'Loading...'
                  : isSignUp
                    ? 'Create Account & Send Link'
                    : 'Send Sign-In Link'}
              </button>

              <p className="text-xs text-slate-500 text-center mt-2">
                {isSignUp
                  ? "No password needed! We'll email you a secure link to complete your sign up"
                  : "We'll email you a secure link to sign in instantly"}
              </p>
            </>
          )}
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp)
              setShowTypeSelection(true)
              setUserType('')
            }}
            className="text-slate-700 hover:underline text-sm"
          >
            {isSignUp
              ? 'Already have an account? Sign In'
              : "Don't have an account? Sign Up"}
          </button>
        </div>
      </div>
      </div>
    </>
  )
}
